import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    MapPinIcon,
    CurrencyEuroIcon,
    BellIcon,
    VideoCameraIcon,
    UserCircleIcon,
    CreditCardIcon,
    DocumentTextIcon,
    LinkIcon,
    BookOpenIcon,
    ChevronDownIcon,
    SignalIcon,
    ArrowTopRightOnSquareIcon,
    Squares2X2Icon,
    PlayCircleIcon,
    CalendarDaysIcon,
    RectangleGroupIcon,
    CameraIcon,
    ClockIcon,
    CheckBadgeIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { APP_VERSION_LABEL, APP_BUILD_DATE } from "./version";

/**
 * Modèle de navigation (data-driven) — un seul endroit à éditer pour ajouter/retirer une entrée.
 * Un noeud est soit un lien ({ to } / { href }), soit un groupe ({ children }).
 * IA calquée sur les consoles signage pro (MagicInfo/Yodeck) : Vue d'ensemble -> Écrans ->
 * Diffusion -> Contenu -> métiers -> réseau -> ressources. Les ROUTES restent inchangées.
 */
const NAV = [
    {
        title: "Vue d'ensemble",
        items: [
            { label: "Dashboard", icon: Squares2X2Icon, to: "/home" },
            { label: "Now Playing", icon: PlayCircleIcon, to: "/nowplaying", live: true },
            { label: "Carte des players", icon: MapPinIcon, to: "/mapplayers" },
        ],
    },
    {
        title: "Écrans",
        items: [
            { label: "Écrans approuvés", icon: CheckBadgeIcon, to: "/listtizenbo" },
            { label: "En attente d'approbation", icon: ExclamationTriangleIcon, to: "/listnonapprouve" },
            { label: "Murs d'écrans", icon: RectangleGroupIcon, to: "/listvideowall" },
            { label: "Captures d'écran", icon: CameraIcon, to: "/imagemanager" },
        ],
    },
    {
        title: "Diffusion",
        items: [
            {
                label: "Programmes",
                icon: CalendarDaysIcon,
                children: [
                    { label: "Vue cartes", to: "/listclientprogramme" },
                    { label: "Vue liste", to: "/listprogramme" },
                ],
            },
            { label: "Historique déploiements", icon: ClockIcon, to: "/deployment-history" },
        ],
    },
    {
        title: "Contenu",
        items: [
            {
                label: "Médiathèque",
                icon: VideoCameraIcon,
                children: [
                    { label: "Gestion des fichiers", to: "/filemanager" },
                    { label: "Uploader un média", to: "/uploadfile" },
                ],
            },
        ],
    },
    {
        title: "Métiers",
        items: [
            {
                label: "Price-Eat",
                icon: CurrencyEuroIcon,
                children: [
                    { label: "Utilisateurs", to: "/listusers" },
                    { label: "Articles & prix", to: "/listidpdv" },
                ],
            },
            { label: "Call-Eat", icon: BellIcon, to: "/listcalleat" },
            { label: "Perso Call-Eat", icon: BellIcon, to: "/perso-calleat" },
            {
                label: "Alta Fid",
                icon: CreditCardIcon,
                children: [{ label: "PDV Alta Fid", to: "/listaltafidpdv" }],
            },
        ],
    },
    {
        title: "Réseau & Annuaire",
        items: [
            {
                label: "Annuaire",
                icon: UserCircleIcon,
                // Clic sur l'en-tête -> va directement sur Client (raccourci demandé) ; l'arbre se
                // déroule ensuite tout seul jusqu'à Client via l'effet d'ouverture sur la route active.
                to: "/annuaireclient",
                children: [
                    {
                        label: "Samsung / LG",
                        children: [
                            { label: "Client", to: "/annuaireclient" },
                            { label: "Serveur", to: "/annuaireserver" },
                        ],
                    },
                    {
                        label: "Wireguard",
                        children: [
                            { label: "Wireguard", to: "/wireguard" },
                            { label: "Client", to: "/listclientwireguard" },
                        ],
                    },
                    { label: "Annuaire Alta", to: "/annuairealtaclient" },
                ],
            },
        ],
    },
    {
        title: "Ressources",
        items: [
            { label: "Documentation", icon: DocumentTextIcon, to: "/documentation" },
            { label: "Liens de téléchargement", icon: LinkIcon, to: "/listlink" },
            {
                label: "Comptes Pad",
                icon: BookOpenIcon,
                href:
                    "https://graphcom92-my.sharepoint.com/:x:/r/personal/younes_abdoun_graphcom_fr/_layouts/15/doc2.aspx?sourcedoc=%7B2a409c51-5eb2-4926-9da9-b7e29b371403%7D&action=edit&activeCell=%27Signage%27!E556&wdinitialsession=554cf1e6-1252-794f-8c15-b542228a2d42&wdrldsc=8&wdrldc=1&wdrldr=AccessTokenExpiredWarningUnauthenticated%2CRefreshin",
            },
        ],
    },
];

const isPathActive = (to, pathname) =>
    to && (pathname === to || pathname.startsWith(to + "/"));

const subtreeHasActive = (node, pathname) => {
    // Un nœud peut avoir À LA FOIS `to` (en-tête-lien) ET `children` : actif si sa propre route OU
    // le sous-arbre d'un enfant est actif (pas de `return` anticipé sur `to`).
    if (node.to && isPathActive(node.to, pathname)) return true;
    if (node.children) return node.children.some((c) => subtreeHasActive(c, pathname));
    return false;
};

/** Clés (chemins de labels) des groupes ouverts par défaut = ceux contenant la route active. */
const openKeysForPath = (pathname) => {
    const keys = new Set();
    const walk = (node, prefix) => {
        const key = `${prefix}/${node.label}`;
        if (node.children) {
            if (node.children.some((c) => subtreeHasActive(c, pathname))) keys.add(key);
            node.children.forEach((c) => walk(c, key));
        }
    };
    NAV.forEach((section) =>
        section.items.forEach((item) => walk(item, section.title))
    );
    return keys;
};

function NavLeaf({ node, depth, pathname }) {
    const active = isPathActive(node.to, pathname);
    const Icon = node.icon;
    const content = (
        <>
            {depth === 0 && Icon ? (
                <Icon className="h-5 w-5 shrink-0" />
            ) : (
                <span
                    className={`ml-1 h-1.5 w-1.5 shrink-0 rounded-full transition ${
                        active ? "bg-indigo-400" : "bg-slate-600 group-hover/leaf:bg-slate-400"
                    }`}
                />
            )}
            <span className="flex-1 truncate">{node.label}</span>
            {node.live && (
                <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
            )}
            {node.href && (
                <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-slate-500" />
            )}
        </>
    );

    const cls = `group/leaf flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
        active
            ? "bg-indigo-500/15 font-medium text-white"
            : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
    }`;

    if (node.href) {
        return (
            <a href={node.href} target="_blank" rel="noreferrer" className={cls}>
                {content}
            </a>
        );
    }
    return (
        <Link to={node.to} className={cls}>
            {active && depth === 0 && (
                <span className="absolute left-0 h-6 w-1 -translate-x-3 rounded-r bg-indigo-400" />
            )}
            {content}
        </Link>
    );
}

function NavGroup({ node, depth, pathname, openKeys, toggle, keyPrefix }) {
    const key = `${keyPrefix}/${node.label}`;
    const open = openKeys.has(key);
    const Icon = node.icon;
    const containsActive = subtreeHasActive(node, pathname);

    const headerCls = `flex flex-1 items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
        containsActive && !open
            ? "text-indigo-200"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
    }`;
    const iconEl = depth === 0 && Icon
        ? <Icon className="h-5 w-5 shrink-0" />
        : <span className="ml-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />;

    return (
        <div>
            <div className="flex items-center">
                {node.to ? (
                    // En-tête-lien : NAVIGUE vers node.to ET ouvre le groupe. La route change ->
                    // l'effet openKeysForPath déroule tout le chemin jusqu'à la page active.
                    <Link to={node.to} onClick={() => { if (!open) toggle(key); }} className={headerCls}>
                        {iconEl}
                        <span className="flex-1 text-left font-medium">{node.label}</span>
                    </Link>
                ) : (
                    <button type="button" onClick={() => toggle(key)} className={headerCls}>
                        {iconEl}
                        <span className="flex-1 text-left font-medium">{node.label}</span>
                    </button>
                )}
                {/* Chevron = toggle indépendant (permet de replier même un en-tête-lien). */}
                <button
                    type="button"
                    onClick={() => toggle(key)}
                    aria-label={open ? "Replier" : "Déplier"}
                    className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"
                >
                    <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                            open ? "rotate-180 text-indigo-300" : "text-slate-500"
                        }`}
                    />
                </button>
            </div>
            {open && (
                <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
                    {node.children.map((child) => (
                        <NavNode
                            key={child.label}
                            node={child}
                            depth={depth + 1}
                            pathname={pathname}
                            openKeys={openKeys}
                            toggle={toggle}
                            keyPrefix={key}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function NavNode(props) {
    if (props.node.children) return <NavGroup {...props} />;
    return (
        <div className="relative">
            <NavLeaf node={props.node} depth={props.depth} pathname={props.pathname} />
        </div>
    );
}

export default function DefaultSidebar({ open = false, onClose = () => {} }) {
    const { pathname } = useLocation();
    const [openKeys, setOpenKeys] = useState(() => openKeysForPath(pathname));

    // Ouvre automatiquement le groupe contenant la page courante à chaque navigation.
    useEffect(() => {
        setOpenKeys((prev) => {
            const next = new Set(prev);
            openKeysForPath(pathname).forEach((k) => next.add(k));
            return next;
        });
    }, [pathname]);

    // Ferme le drawer mobile à chaque navigation (clic sur un lien). Sans effet visuel sur desktop
    // (la sidebar y est toujours affichée via lg:translate-x-0).
    useEffect(() => { onClose(); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (key) =>
        setOpenKeys((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    return (
        <>
            {/* Voile mobile : ferme le drawer au clic (au-dessus du contenu, sous la sidebar). */}
            {open && <div className="fixed inset-0 top-16 z-30 bg-slate-900/50 backdrop-blur-sm lg:hidden" onClick={onClose} aria-hidden="true" />}
            <aside className={`drawer-mobile sidebar-scroll fixed top-16 left-0 z-40 flex h-[calc(100vh-4rem)] w-72 shrink-0 flex-col overflow-y-auto border-r border-white/5 bg-gradient-to-b from-slate-900 via-slate-900 to-indigo-950 px-3 py-5 transition-transform duration-300 ease-in-out lg:sticky lg:z-auto ${open ? 'is-open shadow-2xl' : 'lg:shadow-none'}`}>
            {/* Marque */}
            <div className="mb-6 flex items-center gap-3 px-2">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30">
                    <SignalIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                    <p className="text-sm font-bold leading-tight text-white">
                        GRAPH<span className="text-indigo-400">COM</span>
                    </p>
                    <p className="text-[11px] text-slate-500">Signage Console</p>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-6">
                {NAV.map((section) => (
                    <div key={section.title}>
                        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            {section.title}
                        </p>
                        <div className="space-y-1">
                            {section.items.map((item) => (
                                <NavNode
                                    key={item.label}
                                    node={item}
                                    depth={0}
                                    pathname={pathname}
                                    openKeys={openKeys}
                                    toggle={toggle}
                                    keyPrefix={section.title}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Pied */}
            <div className="mt-6 rounded-xl border border-white/5 bg-white/5 px-3 py-3">
                <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
                    <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 shadow shadow-emerald-400/50" />
                        Système opérationnel
                    </span>
                    <span
                        title={`Version du CMS — ${APP_BUILD_DATE}`}
                        className="rounded-md bg-white/5 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-500"
                    >
                        {APP_VERSION_LABEL}
                    </span>
                </div>
            </div>
            </aside>
        </>
    );
}
